import React, {useState} from 'react';
import axios from 'axios';
import {
    Button,
    Card,
    CardHeader,
    CardBody,
    FormGroup,
    Form,
    Input,
    InputGroupAddon,
    InputGroupText,
    InputGroup,
    Row,
    Col,
} from "reactstrap";
import zxcvbn from 'zxcvbn';
import {getCookie, validateEmail} from "tools/tools"
import InfoModal from "../../components/Modals/InfoModal";
import {Loader} from "@skbkontur/react-ui";
import {useTranslation} from "react-i18next";

const Register = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [uploading, setUploading] = useState(false);
    const [repeatPassword, setRepeatPassword] = useState('');
    const [passwordStrength, setPasswordStrength] = useState(null);
    const [isAgree, setIsAgree] = useState(false);
    const [registrationErrors, setRegistrationErrors] = useState(null);
    const [errorModalIsOpen, setErrorModalIsOpen] = useState(false);
    const [successfulModalIsOpen, setSuccessfulModalIsOpen] = useState(false);
    const {t, i18n} = useTranslation();
    const [errors, setErrors] = useState({
        name: '',
        email: '',
        password: '',
        repeatPassword: '',
    });

    const csrfToken = getCookie('csrftoken');

    const handlePasswordChange = (e) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        const strength = zxcvbn(newPassword);
        setPasswordStrength(strength.score);
        setErrors({...errors, password: '', repeatPassword: ''});
    };

    const handleRepeatPasswordChange = (e) => {
        setRepeatPassword(e.target.value);
        setErrors({...errors, repeatPassword: ''});
    };

    const validateForm = () => {
        let newErrors = {name: '', email: '', password: '', repeatPassword: ''};
        if (!name) newErrors.name = 'Name is required';
        if (!email) {
            newErrors.email = 'Email is required';
        } else if (!validateEmail(email)) {
            newErrors.email = 'Invalid email address';
        }
        if (!password) newErrors.password = 'Password is required';
        if (password !== repeatPassword) newErrors.repeatPassword = 'Passwords do not match';
        setErrors(newErrors);
        return Object.values(newErrors).every(error => !error);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            try {
                setUploading(true)
                var bodyFormData = new FormData();
                bodyFormData.append('username', name);
                bodyFormData.append('email', email);
                bodyFormData.append('password', password);
                bodyFormData.append('repeated_password', repeatPassword);
                const response = await axios.post('/api/register/', bodyFormData, {
                    headers: {
                        'X-CSRFToken': csrfToken,
                        'Content-Type': 'multipart/form-data',
                    },
                    withCredentials: true,
                });
                console.log(response)
                setUploading(false)
                setSuccessfulModalIsOpen(true)
            } catch (error) {
                console.log(error)
                setRegistrationErrors(error.response.data['errors'])
                setUploading(false)
                setErrorModalIsOpen(true)
            }
        }
    };

    const getPasswordStrengthLabel = (score) => {
        switch (score) {
            case 0:
                return 'Very Weak';
            case 1:
                return 'Weak';
            case 2:
                return 'Fair';
            case 3:
                return 'Good';
            case 4:
                return 'Strong';
            default:
                return '';
        }
    };

    const getPasswordStrengthClass = (score) => {
        switch (score) {
            case 0:
            case 1:
                return 'text-danger';
            case 2:
                return 'text-warning';
            case 3:
            case 4:
                return 'text-success';
            default:
                return '';
        }
    };

    const inputClass = (error) => error ? 'is-invalid' : '';


    const errorModalBody = () => {
        return registrationErrors == null || registrationErrors.length === 0 ? ('error') : (<ul>
            {Object.keys(registrationErrors).map((key, index) => (
                <li key={index}>
                    <a target="_blank" rel="noopener noreferrer">
                        <b>{key}:</b> {registrationErrors[key][0]}
                    </a>

                </li>
            ))}
        </ul>)
    }

    const successfulModalBody = () =>
        (
            <h3>A registration confirmation link has been sent to your email address. ({email})</h3>
        );

    const registerForm = () => (
        <CardBody className="px-lg-5 py-lg-5">
            <div className="text-center text-muted mb-4">
                <small>Sign up with credentials</small>
            </div>
            <Form role="form" onSubmit={handleSubmit}>
                <FormGroup>
                    <InputGroup className={`input-group-alternative mb-3 ${inputClass(errors.name)}`}>
                        <InputGroupAddon addonType="prepend">
                            <InputGroupText>
                                <i className="ni ni-hat-3"/>
                            </InputGroupText>
                        </InputGroupAddon>
                        <Input
                            placeholder="Name"
                            type="text"
                            value={name}
                            name="username"
                            onChange={(e) => {
                                setName(e.target.value);
                                setErrors({...errors, name: ''});
                            }}
                        />
                    </InputGroup>
                    {errors.name && <div className="text-danger">{errors.name}</div>}
                </FormGroup>
                <FormGroup>
                    <InputGroup className={`input-group-alternative mb-3 ${inputClass(errors.email)}`}>
                        <InputGroupAddon addonType="prepend">
                            <InputGroupText>
                                <i className="ni ni-email-83"/>
                            </InputGroupText>
                        </InputGroupAddon>
                        <Input
                            placeholder="Email"
                            type="email"
                            autoComplete="new-email"
                            name="email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                setErrors({...errors, email: ''});
                            }}
                        />
                    </InputGroup>
                    {errors.email && <div className="text-danger">{errors.email}</div>}
                </FormGroup>
                <FormGroup>
                    <InputGroup className={`input-group-alternative ${inputClass(errors.password)}`}>
                        <InputGroupAddon addonType="prepend">
                            <InputGroupText>
                                <i className="ni ni-lock-circle-open"/>
                            </InputGroupText>
                        </InputGroupAddon>
                        <Input
                            placeholder="Password"
                            type="password"
                            name="password"
                            autoComplete="new-password"
                            value={password}
                            onChange={handlePasswordChange}
                        />
                    </InputGroup>
                    {errors.password && <div className="text-danger">{errors.password}</div>}
                </FormGroup>
                <FormGroup>
                    <InputGroup className={`input-group-alternative ${inputClass(errors.repeatPassword)}`}>
                        <InputGroupAddon addonType="prepend">
                            <InputGroupText>
                                <i className="ni ni-lock-circle-open"/>
                            </InputGroupText>
                        </InputGroupAddon>
                        <Input
                            placeholder="Repeat-password"
                            type="password"
                            autoComplete="new-repeat-password"
                            name="repeated_password"
                            value={repeatPassword}
                            onChange={handleRepeatPasswordChange}
                        />
                    </InputGroup>
                    {errors.repeatPassword && <div className="text-danger">{errors.repeatPassword}</div>}
                </FormGroup>
                <div className="text-muted font-italic">
                    {password && (
                        <div>
                            <small className={getPasswordStrengthClass(passwordStrength)}>
                                Password strength: {getPasswordStrengthLabel(passwordStrength)}
                            </small>
                        </div>
                    )}
                </div>
                <Row className="my-4">
                    <Col xs="12">
                        <div className="custom-control custom-control-alternative custom-checkbox">
                            <input
                                className="custom-control-input"
                                id="customCheckRegister"
                                type="checkbox"
                                defaultChecked={isAgree}
                                onChange={() => setIsAgree((state) => !state)}
                            />
                            <label
                                className="custom-control-label"
                                htmlFor="customCheckRegister"
                            >
                              <span className="text-muted">
                                I agree with the{" "}
                                  <a href="#pablo" onClick={(e) => e.preventDefault()}>
                                  Privacy Policy
                                </a>
                              </span>
                            </label>
                        </div>
                    </Col>
                </Row>
                <div className="text-center">
                    <Button className="mt-4" color="primary" type="submit" disabled={!isAgree}>
                        Create account
                    </Button>
                </div>
            </Form>
        </CardBody>
    )


    return (
        <>
            <InfoModal
                isOpen={errorModalIsOpen}
                modalTitle={'Errors occurred during registration'}
                onRequestClose={() => setErrorModalIsOpen(false)}
                body={errorModalBody}
                contentLabel="Error modal">
            </InfoModal>
            <InfoModal
                isOpen={successfulModalIsOpen}
                modalTitle={'Registration was successful'}
                onRequestClose={() => setSuccessfulModalIsOpen(false)}
                body={successfulModalBody}
                contentLabel="Success modal">
            </InfoModal>

            <Col lg="6" md="8">
                <Card className="bg-secondary shadow border-0">
                    {/*<CardHeader className="bg-transparent pb-5">*/}
                    {/*  <div className="text-muted text-center mt-2 mb-4">*/}
                    {/*    <small>Sign up with</small>*/}
                    {/*  </div>*/}
                    {/*  <div className="text-center">*/}
                    {/*    <Button*/}
                    {/*      className="btn-neutral btn-icon mr-4"*/}
                    {/*      color="default"*/}
                    {/*      href="#pablo"*/}
                    {/*      onClick={(e) => e.preventDefault()}*/}
                    {/*    >*/}
                    {/*      <span className="btn-inner--icon">*/}
                    {/*        <img*/}
                    {/*          alt="..."*/}
                    {/*          src={*/}
                    {/*            require("../../assets/img/icons/common/github.svg")*/}
                    {/*              .default*/}
                    {/*          }*/}
                    {/*        />*/}
                    {/*      </span>*/}
                    {/*      <span className="btn-inner--text">Github</span>*/}
                    {/*    </Button>*/}
                    {/*    <Button*/}
                    {/*      className="btn-neutral btn-icon"*/}
                    {/*      color="default"*/}
                    {/*      href="#pablo"*/}
                    {/*      onClick={(e) => e.preventDefault()}*/}
                    {/*    >*/}
                    {/*      <span className="btn-inner--icon">*/}
                    {/*        <img*/}
                    {/*          alt="..."*/}
                    {/*          src={*/}
                    {/*            require("../../assets/img/icons/common/google.svg")*/}
                    {/*              .default*/}
                    {/*          }*/}
                    {/*        />*/}
                    {/*      </span>*/}
                    {/*      <span className="btn-inner--text">Google</span>*/}
                    {/*    </Button>*/}
                    {/*  </div>*/}
                    {/*</CardHeader>*/}

                    {uploading ? (<Loader active={uploading}>
                        {registerForm()}
                    </Loader>) : (registerForm())}

                </Card>
            </Col>
        </>
    );
};

export default Register;
